import React, { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { ArrowRight, Building2, Hammer, Search, Share2, ShieldCheck, Wallet } from 'lucide-react';
import {
  FacebookIcon,
  FacebookShareButton,
  LinkedinIcon,
  LinkedinShareButton,
  TwitterIcon,
  TwitterShareButton,
  WhatsappIcon,
  WhatsappShareButton,
} from 'react-share';
import {
  SELF_BUILD_BED_TYPES,
  selfBuildCountySlugs,
  toSelfBuildSlug,
  toTitleCase,
} from '../../data/selfBuild2026';

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-IE', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(value || 0);

const SCSI_SEED = 371311;

const SelfBuildDiscoverPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCounty, setSelectedCounty] = useState('dublin');
  const [selectedBeds, setSelectedBeds] = useState<2 | 3>(3);

  const filteredCounties = useMemo(() => {
    const normalized = searchTerm.toLowerCase().trim();
    if (!normalized) return selfBuildCountySlugs;
    return selfBuildCountySlugs.filter((county) => county.includes(normalized));
  }, [searchTerm]);

  const selectedSlug = toSelfBuildSlug(selectedCounty, selectedBeds);
  const selectedPath = `/self-build/${selectedSlug}`;

  const canonicalUrl = 'https://rentsmart.ie/self-build';
  const shareUrl = typeof window !== 'undefined' ? window.location.href : canonicalUrl;
  const shareText =
    'Cost to build a 2 or 3-bed house by county in Ireland (2026). Compare self-build estimates on RentSmart.ie.';

  return (
    <div className="relative min-h-screen w-screen max-w-none left-1/2 right-1/2 ml-[-50vw] mr-[-50vw] overflow-x-hidden bg-slate-50">
      <Helmet>
        <title>Self-Build House Cost Calculator Ireland (2026)</title>
        <meta
          name="description"
          content="Estimate the cost to build a 2-bed or 3-bed house in every Irish county in 2026, seeded from the latest SCSI benchmark (€371,311)."
        />
        <meta
          name="keywords"
          content="cost to build house ireland 2026, self build cost county ireland, 2 bed build cost, 3 bed build cost, scsi benchmark 371311"
        />
        <link rel="canonical" href={canonicalUrl} />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: 'Self-Build House Cost by County (2026)',
            description:
              'Programmatic county-by-county estimates for 2-bed and 3-bed self-build costs in Ireland.',
            url: canonicalUrl,
          })}
        </script>
      </Helmet>

      <header className="bg-slate-900 text-white pt-14 pb-20 px-4 relative overflow-hidden">
        <div className="absolute -top-12 -right-8 h-80 w-80 rounded-full bg-emerald-500/20 blur-3xl" />
        <div className="absolute -bottom-20 -left-8 h-72 w-72 rounded-full bg-sky-500/20 blur-3xl" />

        <div className="relative max-w-7xl mx-auto">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-black uppercase tracking-widest mb-5">
            <ShieldCheck className="h-4 w-4" /> 2026 Self-Build Intelligence
          </div>
          <h1 className="text-4xl md:text-6xl font-black tracking-tight mb-4">
            Estimated Cost to Build a 2, 3-Bed House in Irish counties (2026)
          </h1>
          <p className="text-slate-300 max-w-3xl text-lg leading-relaxed mb-7">
            Deterministic county-level self-build estimates seeded from the current SCSI benchmark of{' '}
            {formatCurrency(SCSI_SEED)}.
          </p>

          <div className="rounded-[2rem] border border-white/10 bg-white/5 backdrop-blur p-5 md:p-7">
            <div className="grid md:grid-cols-12 gap-5 items-end">
              <div className="md:col-span-5">
                <label className="text-[10px] uppercase tracking-wider font-black text-slate-300 mb-2 block">
                  Select County
                </label>
                <select
                  value={selectedCounty}
                  onChange={(event) => setSelectedCounty(event.target.value)}
                  className="w-full rounded-xl bg-white text-slate-900 px-4 py-3 font-semibold outline-none focus:ring-4 focus:ring-emerald-200"
                >
                  {selfBuildCountySlugs.map((county) => (
                    <option key={county} value={county}>
                      {toTitleCase(county)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-4">
                <label className="text-[10px] uppercase tracking-wider font-black text-slate-300 mb-2 block">
                  House Type
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {SELF_BUILD_BED_TYPES.map((beds) => (
                    <button
                      key={beds}
                      type="button"
                      onClick={() => setSelectedBeds(beds)}
                      className={`rounded-xl border px-4 py-3 text-sm font-black transition-all ${
                        selectedBeds === beds
                          ? 'border-emerald-300 bg-emerald-500 text-white'
                          : 'border-white/30 bg-white text-slate-900 hover:border-emerald-200'
                      }`}
                    >
                      {beds}-Bed
                    </button>
                  ))}
                </div>
              </div>
              <div className="md:col-span-3">
                <Link
                  to={selectedPath}
                  className="no-underline inline-flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-500 px-4 py-3 text-sm font-black text-white hover:bg-emerald-600"
                >
                  Open Report <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>

          <div className="mt-5 flex flex-wrap items-center gap-3">
            <span className="text-xs font-black uppercase tracking-widest text-slate-400">Share:</span>
            <FacebookShareButton url={shareUrl} hashtag="#RentSmartIE">
              <FacebookIcon size={30} round />
            </FacebookShareButton>
            <TwitterShareButton url={shareUrl} title={shareText}>
              <TwitterIcon size={30} round />
            </TwitterShareButton>
            <WhatsappShareButton url={shareUrl} title={shareText}>
              <WhatsappIcon size={30} round />
            </WhatsappShareButton>
            <LinkedinShareButton url={shareUrl} title="Self-Build Cost Ireland 2026" summary={shareText}>
              <LinkedinIcon size={30} round />
            </LinkedinShareButton>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-10">
        <section className="grid md:grid-cols-3 gap-4 mb-8">
          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <Building2 className="h-5 w-5 text-emerald-600 mb-2" />
            <h2 className="text-sm font-black text-slate-900 mb-1">County-Adjusted Model</h2>
            <p className="text-sm text-slate-600">
              Local multipliers are applied to the benchmark seed for county-sensitive estimates.
            </p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <Hammer className="h-5 w-5 text-sky-600 mb-2" />
            <h2 className="text-sm font-black text-slate-900 mb-1">2-Bed and 3-Bed Profiles</h2>
            <p className="text-sm text-slate-600">
              Compare build cost envelopes by bed type with deterministic breakouts.
            </p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <Wallet className="h-5 w-5 text-amber-600 mb-2" />
            <h2 className="text-sm font-black text-slate-900 mb-1">Mortgage-Equivalent Signal</h2>
            <p className="text-sm text-slate-600">
              Use the monthly equivalent to benchmark buy vs rent decision pressure.
            </p>
          </div>
        </section>

        <section className="rounded-[2rem] border border-slate-200 bg-white p-6 mb-8">
          <div className="relative max-w-xl mb-6">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <input
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search county (e.g. dublin, cork, galway)"
              className="w-full rounded-xl border border-slate-200 bg-slate-50 text-slate-900 pl-12 pr-4 py-3 outline-none focus:ring-4 focus:ring-cyan-100"
            />
          </div>

          <h2 className="text-2xl font-black text-slate-900 mb-4">Popular Self-Build Reports</h2>
          {filteredCounties.length === 0 ? (
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-8 text-center text-slate-500">
              No counties match your search.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredCounties.slice(0, 18).flatMap((countySlug) =>
                SELF_BUILD_BED_TYPES.map((beds) => {
                  const slug = toSelfBuildSlug(countySlug, beds);
                  return (
                    <Link
                      key={slug}
                      to={`/self-build/${slug}`}
                      className="no-underline rounded-2xl border border-slate-200 bg-white p-5 hover:-translate-y-1 hover:shadow-lg transition-all"
                    >
                      <p className="text-[10px] uppercase tracking-wider font-black text-slate-500 mb-1">
                        Self-Build Cost {beds}-Bed
                      </p>
                      <p className="text-xl font-black text-slate-900 mb-1">{toTitleCase(countySlug)}</p>
                      <p className="text-sm font-semibold text-slate-600 mb-3">
                        Cost to build a {beds}-bed house in 2026
                      </p>
                      <p className="inline-flex items-center gap-1 text-xs font-black uppercase tracking-wider text-emerald-700">
                        Open Estimate <ArrowRight className="h-3 w-3" />
                      </p>
                    </Link>
                  );
                })
              )}
            </div>
          )}
        </section>

        <section className="rounded-2xl border border-amber-200 bg-amber-50 p-5 mb-8">
          <p className="text-xs text-amber-900 font-semibold leading-relaxed">
            These are planning estimates. Always validate final self-build budgets with professional
            quantity surveyors, local planning constraints, financing terms, and contractor quotes.
          </p>
        </section>

        <section className="rounded-3xl border border-slate-200 bg-gradient-to-br from-white to-emerald-50 p-6">
          <h2 className="text-xl font-black text-slate-900 mb-2 flex items-center gap-2">
            <Share2 className="h-5 w-5 text-emerald-600" /> Share RentSmart.ie
          </h2>
          <p className="text-sm text-slate-600 font-medium mb-4 leading-relaxed">
            Help more buyers discover county-by-county self-build costs on RentSmart.ie.
          </p>
          <div className="flex items-center gap-2">
            <FacebookShareButton url={shareUrl} hashtag="#RentSmartIE">
              <FacebookIcon size={32} round />
            </FacebookShareButton>
            <TwitterShareButton url={shareUrl} title={shareText}>
              <TwitterIcon size={32} round />
            </TwitterShareButton>
            <WhatsappShareButton url={shareUrl} title={shareText}>
              <WhatsappIcon size={32} round />
            </WhatsappShareButton>
            <LinkedinShareButton url={shareUrl} title="Self-Build Cost Ireland 2026" summary={shareText}>
              <LinkedinIcon size={32} round />
            </LinkedinShareButton>
          </div>
        </section>
      </main>
    </div>
  );
};

export default SelfBuildDiscoverPage;
